import React, {
    ReactElement, useEffect, useState,
} from "react";
import {
    MetricsDashboardData, MetricsDashboardDataRow, MetricsDashboardGraphDataSource, MetricsInput, useGetReportsDataQuery,
} from "../../__generated__/api";
import { Loading } from "../../components";
import Agents from "../../components/pickers/agents/Agents";
import DateRangeFilter from "../../components/pickers/date-range/DateRangePicker";

import Priority from "../../components/pickers/priority/Priority";
import Queues from "../../components/pickers/queue/Queue";
import RequestTypes from "../../components/pickers/request-types/RequestTypes";
import Users from "../../components/pickers/users/Users";
import useArticleContext from "../../contexts/HelpContext";
import { Time, parseFilterDates } from "../../date";
import COMPONENT_TO_TYPE from "./graphs";
import ConversationSources from "../../components/pickers/conversation-sources/ConversationSources";
import useRouting, { filterEmptyParameters } from "../../contexts/Routing";
import DateRangeCalendarPicker from "../../components/pickers/date-range/DateRangeCalendarPicker";

function getStartDate() {
    return new Date(new Date().getTime() - (Time.WEEK * 2));
}
function deserializeParams(params: Record<string, unknown>): MetricsInput {
    return filterEmptyParameters({
        ...params,
        startDate: params.startDate ? new Date(params.startDate as string) : getStartDate(),
        endDate: params.endDate ? new Date(params.endDate as string) : undefined,
        formIds: (!params.formIds || Array.isArray(params.formIds)) ? params.formIds : [params.formIds],
        tags: (!params.tags || Array.isArray(params.tags)) ? params.tags : [params.tags],
    } as MetricsInput);
}

export default function Reports(): ReactElement {
    useArticleContext("urn:fq:article:4d37dc318cbd4afa8f0fbacddb883101");

    const { getSearchParams, historyPushSearch, Paths } = useRouting();

    const [filters, setFilters] = useState<MetricsInput>(() => deserializeParams(getSearchParams(Paths.REPORTS) ?? {
        startDate: getStartDate(),
    }));

    const { data, loading: isLoading } = useGetReportsDataQuery({
        variables: {
            input: {
                ...filters,
                endDate: filters.endDate?.toISOString(),
                startDate: filters.startDate?.toISOString(),
            },
        },
    });

    useEffect(() => {
        const params = Object.create(null);
        Object.entries({ ...filters }).forEach(([key, value]) => {
            if (value != null && value !== "" && (!Array.isArray(value) || value.isNotEmpty)) {
                params[key] = value instanceof Date ? value.toISOString() : value;
            }
        });

        historyPushSearch(params);
    }, [filters]);

    const graphs = data?.metricsDashboardWithData?.dashboard.settings.graphs.groupBy("locationY") ?? {};

    const rowGroups = data?.metricsDashboardWithData?.data.toIdMap("dataSource", rowData => {
        const columnIndexMap = rowData?.columns.map((row, index) => ({ id: row.id, index })).toIdMap("id", "index") ?? {};
        const labelMap = rowData?.labels.toIdMap("id", "label") ?? {};
        return {
            rows: rowData,
            columnIndexMap,
            labelMap,
        };
    }) ?? {};
    return (

        <div className="flex-grow-1 d-flex flex-column h-100 shadow">

            <div className={`d-flex mx-3 justify-content-between align-items-center
                        bg-white border-bottom border-gray-400 p-3 mt-3 mb-3 rounded`}
            >
                <h5 className="m-0">
                    Reports
                </h5>
                <div className="d-flex flex-grow-1 align-items-center justify-content-end">
                    <DateRangeCalendarPicker
                        shouldShowIcon={false}
                        dropdownClassname="w-auto border-0"
                        toggleClassName="btn-none text-light-400 p-0 me-4"
                        toggleText="Created"
                        className="text-light-400 p-0 m-0 shadow"
                        value={{
                            startDate: filters.startDate,
                            endDate: filters.endDate,
                        }}
                        onChange={change => {
                            const dates = parseFilterDates(change);
                            setFilters({
                                ...filters,
                                startDate: dates.startDate ? new Date(dates.startDate) : undefined,
                                endDate: dates.endDate ? new Date(dates.endDate) : undefined,
                            });
                        }}
                        // onReset={() => setFilters({ ...filters, startDate: getStartDate(), endDate: undefined })}
                    />
                    <Priority
                        isClearable
                        placeholder="Requester"
                        toggleText="Priority"
                        isMulti
                        toggleClassName="p-0 btn btn-none text-light-400"
                        shouldShowDropdown
                        className="p-0 text-light-400 me-4"
                        value={filters.priorities ?? []}
                        onSelected={priorities => {
                            setFilters({ ...filters, priorities });
                        }}
                    />
                    <Agents
                        isClearable
                        shouldShowDropdown
                        searchMinimum={10}
                        isMulti
                        toggleText="Assignees"
                        toggleClassName="p-0 btn btn-none text-light-400"
                        className="p-0 me-4"
                        value={filters.assigneeIds ?? []}
                        onSelected={assigneeIds => {
                            setFilters({ ...filters, assigneeIds });
                        }}
                    />

                    <Users
                        isClearable
                        placeholder="Requester"
                        toggleText="Requester"
                        shouldShowDropdown
                        isMulti
                        toggleClassName="p-0 btn btn-none text-light-400"
                        className="p-0 text-light-400 me-4"
                        value={filters.requesterIds as string[]}
                        onSelected={requesterIds => {
                            setFilters({ ...filters, requesterIds });
                        }}
                    />
                    <Queues
                        isClearable
                        placeholder="Queues"
                        toggleText="Queues"
                        shouldShowDropdown
                        isMulti
                        shouldFilterMyQueues
                        toggleClassName="p-0 btn btn-none text-light-400"
                        className="p-0 text-light-400 me-4"
                        value={filters.queueIds as string[]}
                        onSelected={queueIds => {
                            setFilters({ ...filters, queueIds });
                        }}
                    />
                    <RequestTypes
                        placeholder="Request Types"
                        toggleText="Request Types"
                        shouldShowDropdown
                        isMulti
                        searchMinimum={20}
                        toggleClassName="p-0 btn btn-none text-light-400"
                        className="p-0 text-light-400 me-4"
                        value={filters.formIds as string[]}
                        onSelected={formIds => {
                            setFilters({ ...filters, formIds });
                        }}
                    />
                    <ConversationSources
                        isMulti
                        shouldShowDropdown
                        shouldIncludeTags={false}
                        shouldIncludeEmail
                        searchMinimum={10}
                        className="p-0 text-light-400 me-3"
                        toggleClassName="text-light-400 p-0 me-3 btn-none"
                        value={filters.conversationSourceIds as string[]}
                        toggleText="Source"
                        onSelected={conversationSourceIds => {
                            setFilters({ ...filters, conversationSourceIds });
                        }}
                    />

                </div>
            </div>

            {isLoading ? (
                <Loading />
            ) : (
                <div className="flex-grow-1 p-3 d-flex flex-column">
                    {!rowGroups[MetricsDashboardGraphDataSource.Tickets]?.rows.rows.isNotEmpty ? (
                        <div className="text-center text-light-400 flex-grow-1 d-flex align-items-center justify-content-center">
                            No data available
                        </div>
                    ) : Object.values(graphs).sortBy(value => value.firstElement.locationY).map(row => {
                        const column = row.sortBy(value => value.locationX);
                        return (
                            <div className="row mb-3 align-items-stretch">
                                {column.map(graph => {
                                    const Component = COMPONENT_TO_TYPE[graph.type];

                                    const dataSource = rowGroups[graph.dataSource];
                                    return (
                                        <div className={`col-${graph.width}`}>
                                            <div className=" bg-white border border-gray-200 p-3 rounded h-100">
                                                {Component
                                                    ? (
                                                        <Component
                                                            graph={graph}
                                                            data={dataSource.rows as MetricsDashboardData}
                                                            columnIndexMap={dataSource.columnIndexMap}
                                                            labelMap={dataSource.labelMap}
                                                        />
                                                    )
                                                    : null}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}

                </div>
            )}
            <div className="text-light w-100 text-center p-2">
                Want even more reports? Check out our
                {" "}
                <a
                    target="_blank"
                    href="https://support.foqal.io/answers/urn:fq:article:8485846808b143df9e1a417d1c834300?search=data+studio"
                    rel="noreferrer"
                    className="text-light link-underline-light"
                >
                    Google Looker Studio Interation here.
                </a>
            </div>
        </div>

    );
}

Reports.defaultProps = {
    children: undefined,
    title: undefined,
};
